import { useState } from 'react';

import { submitDataWithFetch } from 'modules/api';
import { useEventTypes } from 'modules/event-types';
import { Event, EventForm } from 'modules/events';
import { Form } from 'modules/forms';
import { useI18n } from 'modules/i18n';
import { usePathBase, useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { InlineNotification } from 'modules/ui';
import PropTypes from 'prop-types';

import {
    PERSON_GENDER_DIVERSE,
    PERSON_GENDER_FEMALE,
    PERSON_GENDER_MALE,
    PERSON_GENDER_NOT_SPECIFIED,
    PERSON_TITLE_DOCTOR,
    PERSON_TITLE_PROFESSOR,
    PERSON_TITLE_PROFESSOR_WITH_PROMOTION,
} from './constants';
import {
    useMutatePeople,
    useMutatePersonLandingPageMetadata,
    useMutatePersonWithAssociations,
} from './hooks';

const formElements = [
    {
        elementType: 'select',
        attribute: 'gender',
        values: [
            PERSON_GENDER_MALE,
            PERSON_GENDER_FEMALE,
            PERSON_GENDER_DIVERSE,
            PERSON_GENDER_NOT_SPECIFIED,
        ],
        optionsScope: 'gender',
        withEmpty: true,
    },
    {
        elementType: 'select',
        attribute: 'title',
        values: [
            PERSON_TITLE_DOCTOR,
            PERSON_TITLE_PROFESSOR,
            PERSON_TITLE_PROFESSOR_WITH_PROMOTION,
        ],
        optionsScope: 'title',
        withEmpty: true,
    },
    {
        attribute: 'first_name',
        multiLocale: true,
    },
    {
        attribute: 'last_name',
        multiLocale: true,
    },
    {
        attribute: 'birth_name',
        multiLocale: true,
    },
    {
        attribute: 'alias_names',
        multiLocale: true,
    },
    {
        attribute: 'other_first_names',
        multiLocale: true,
    },
    {
        attribute: 'pseudonym_first_name',
        multiLocale: true,
    },
    {
        attribute: 'pseudonym_last_name',
        multiLocale: true,
    },
    {
        attribute: 'use_pseudonym',
        elementType: 'input',
        type: 'checkbox',
    },
    {
        attribute: 'date_of_birth',
        help: 'help_texts.date_field',
    },
    {
        elementType: 'textarea',
        attribute: 'description',
        multiLocale: true,
    },
];

/**
 * Edits a person and nested events, keeping the people and person caches current.
 *
 * @param {Object} props - Person editing context.
 * @param {Object} [props.data] - Existing person; omit to create one.
 * @param {Function} [props.onSubmit] - Called after a successful person save.
 * @param {Function} [props.onCancel] - Cancels editing.
 * @returns {React.ReactElement} Person form or project/event-type loading status.
 */
export default function PersonForm({ data: person, onSubmit, onCancel }) {
    const [isFetching, setIsFetching] = useState(false);
    const { data: eventTypes, isLoading: eventTypesAreLoading } =
        useEventTypes();
    const mutatePeople = useMutatePeople();
    const mutatePersonWithAssociations = useMutatePersonWithAssociations();
    const mutatePersonLandingPageMetadata =
        useMutatePersonLandingPageMetadata();
    const pathBase = usePathBase();
    const { project, isLoading: projectIsLoading, error } = useProject();
    const { t } = useI18n();

    function showEvent(event) {
        return <Event event={event} />;
    }

    if (error) {
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={error.message}
                isClosable={false}
            />
        );
    }
    if (projectIsLoading || !project || eventTypesAreLoading) {
        return <Spinner />;
    }

    const nestedScopeProps =
        eventTypes?.length > 0
            ? [
                  {
                      formComponent: EventForm,
                      formProps: { personId: person?.id },
                      parent: person,
                      scope: 'event',
                      elementRepresentation: showEvent,
                      onDeleteCallback: (id) => {
                          // In case of server error.
                          if (typeof id !== 'number') {
                              return;
                          }

                          mutatePeople(async (people) => {
                              const eventHolder = people?.data[person.id];

                              const updatedPeople = {
                                  ...people,
                                  data: {
                                      ...people?.data,
                                      [person.id]: {
                                          ...eventHolder,
                                          events: eventHolder.events.filter(
                                              (event) => event.id !== id
                                          ),
                                      },
                                  },
                              };
                              delete updatedPeople.data[id];

                              return updatedPeople;
                          });

                          if (person.id) {
                              mutatePersonWithAssociations(person.id);
                              mutatePersonLandingPageMetadata(person.id);
                          }
                      },
                  },
              ]
            : undefined;

    return (
        <div>
            {person && (
                <h3 className="u-mt-none u-mb">{person.display_name}</h3>
            )}

            <Form
                data={person}
                values={{ project_id: project.id }}
                scope="person"
                nestedScopeProps={nestedScopeProps}
                helpTextCode="person_form"
                onSubmit={async (params) => {
                    mutatePeople(async (people) => {
                        const id = params.person.id;
                        setIsFetching(true);
                        const result = await submitDataWithFetch(
                            pathBase,
                            params
                        );
                        const updatedPerson = result.data;

                        // Other stuff that needs to be done after result is returned.
                        setIsFetching(false);
                        if (id) {
                            mutatePersonWithAssociations(id);
                            mutatePersonLandingPageMetadata(id);
                        }

                        if (typeof onSubmit === 'function') {
                            onSubmit();
                        }

                        const updatedPeople = {
                            ...people,
                            data: {
                                ...people?.data,
                                [updatedPerson.id]: updatedPerson,
                            },
                        };
                        return updatedPeople;
                    });
                }}
                onCancel={onCancel}
                submitText="submit"
                elements={formElements}
                fetching={isFetching}
            />
        </div>
    );
}

PersonForm.propTypes = {
    data: PropTypes.object,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
};
